import { useEffect, useRef, useState, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useChatStore } from '../stores/chatStore'
import { useThreadStore } from '../stores/threadStore'
import { useToastStore } from '../stores/toastStore'
import { track } from '../lib/analytics'
import MessageBubble from '../components/MessageBubble'
import ContentItemCard from '../components/ContentItemCard'
import ChatInput from '../components/ChatInput'
import DocsStatus from '../components/DocsStatus'
import AttachmentsPanel from '../components/AttachmentsPanel'
import ThinkingIndicator from '../components/ThinkingIndicator'
import HighlightNav from '../components/HighlightNav'
import HighlightsSheet from '../components/HighlightsSheet'
import { useHighlightStore } from '../stores/highlightStore'
import { useInlineHighlights, findTextRange } from '../hooks/useInlineHighlights'
import SelectionToolbar from '../components/SelectionToolbar'

export default function Conversation() {
  const { threadId } = useParams<{ threadId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const entries = useChatStore((s) => s.entries)
  const streaming = useChatStore((s) => s.streaming)
  const streamingText = useChatStore((s) => s.streamingText)
  const error = useChatStore((s) => s.error)
  const interruptedEntryId = useChatStore((s) => s.interruptedEntryId)
  const truncatedEntryId = useChatStore((s) => s.truncatedEntryId)
  const continuingEntryId = useChatStore((s) => s.continuingEntryId)
  const fetchTimeline = useChatStore((s) => s.fetchTimeline)
  const sendMessage = useChatStore((s) => s.sendMessage)
  const retryStream = useChatStore((s) => s.retryStream)
  const continueStream = useChatStore((s) => s.continueStream)
  const threads = useThreadStore((s) => s.threads)
  const updateThread = useThreadStore((s) => s.updateThread)
  const renameThread = useThreadStore((s) => s.renameThread)
  const deleteThread = useThreadStore((s) => s.deleteThread)
  const scrollRef = useRef<HTMLDivElement>(null)
  const entryRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const isAtBottomRef = useRef<boolean>(true)
  const userHasScrolledRef = useRef<boolean>(false)
  const [showScrollPill, setShowScrollPill] = useState(false)
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleValue, setTitleValue] = useState('')
  const titleInputRef = useRef<HTMLInputElement>(null)
  const [autoRenaming, setAutoRenaming] = useState(false)
  const [previousTitle, setPreviousTitle] = useState<string | null>(null)
  const suppressBlurRef = useRef(false)
  const highlights = useHighlightStore((s) => s.highlights)
  const fetchHighlights = useHighlightStore((s) => s.fetchHighlights)
  const createHighlight = useHighlightStore((s) => s.createHighlight)
  const updateHighlight = useHighlightStore((s) => s.updateHighlight)
  const deleteHighlight = useHighlightStore((s) => s.deleteHighlight)
  useInlineHighlights(scrollRef, highlights, entries.length)
  const [highlightsOpen, setHighlightsOpen] = useState(false)
  const [attachmentsOpen, setAttachmentsOpen] = useState(false)
  const [showModelPicker, setShowModelPicker] = useState(false)
  const [availableModels, setAvailableModels] = useState<string[]>([])
  const [showPresetPicker, setShowPresetPicker] = useState(false)
  const presets = useThreadStore((s) => s.presets)
  const fetchPresets = useThreadStore((s) => s.fetchPresets)

  useEffect(() => {
    useThreadStore.getState().fetchModels().then((models) => {
      setAvailableModels(models.map((m) => m.id))
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (presets.length === 0) fetchPresets()
  }, [presets.length, fetchPresets])

  // Highlight state from search
  const highlightParam = searchParams.get('highlight')
  const highlightEntryId = highlightParam ? Number(highlightParam) : null
  const [highlightActive, setHighlightActive] = useState(!!highlightEntryId)
  const [currentHighlightIndex, setCurrentHighlightIndex] = useState(0)
  const [hasScrolledToHighlight, setHasScrolledToHighlight] = useState(false)

  // Track if we came from search (for back button behavior)
  const cameFromSearch = !!highlightParam

  const thread = threads.find((t) => t.id === Number(threadId))
  const id = Number(threadId)

  // All entry IDs for highlight navigation (one per timeline entry)
  const entryIds = entries.map((e) => e.id)

  const handleTitleTap = useCallback(() => {
    if (!thread) return
    setTitleValue(thread.title)
    setEditingTitle(true)
    setTimeout(() => titleInputRef.current?.focus(), 50)
  }, [thread])

  const commitTitle = useCallback(() => {
    if (suppressBlurRef.current) {
      suppressBlurRef.current = false
      return
    }
    const trimmed = titleValue.trim()
    if (trimmed && thread && trimmed !== thread.title) {
      updateThread(thread.id, { title: trimmed })
      track('thread_renamed', { thread_id: thread.id, method: 'manual' })
    }
    setEditingTitle(false)
  }, [titleValue, thread, updateThread])

  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitTitle()
    } else if (e.key === 'Escape') {
      setEditingTitle(false)
    }
  }, [commitTitle])

  const handleAutoRename = useCallback(async () => {
    if (!thread) return
    const oldTitle = thread.title
    setAutoRenaming(true)
    try {
      const newTitle = await renameThread(thread.id)
      setTitleValue(newTitle)
      setPreviousTitle(oldTitle)
      track('thread_renamed', { thread_id: thread.id, method: 'auto' })
    } catch {
      useToastStore.getState().addToast('Auto-rename failed. Try again.', 'error')
    } finally {
      setAutoRenaming(false)
      suppressBlurRef.current = false
    }
  }, [thread, renameThread])

  const handleRevertTitle = useCallback(() => {
    if (!thread || !previousTitle) return
    suppressBlurRef.current = false
    updateThread(thread.id, { title: previousTitle })
    setTitleValue(previousTitle)
    setPreviousTitle(null)
    setEditingTitle(false)
  }, [thread, previousTitle, updateThread])

  // Track current entries and streaming state via refs so the unmount cleanup reads fresh values
  const entriesRef = useRef(entries)
  const streamingRef = useRef(streaming)
  // Track whether any content import was initiated — prevents auto-delete racing with pending ingestion
  const hasImportedRef = useRef(false)
  // Gate the auto-delete cleanup until we've actually fetched the timeline.
  // Without this, React StrictMode's dev-mode double-invoke fires the cleanup
  // before the first fetch resolves and incorrectly deletes the thread.
  const timelineLoadedRef = useRef(false)
  useEffect(() => { entriesRef.current = entries }, [entries])
  useEffect(() => { streamingRef.current = streaming }, [streaming])

  useEffect(() => {
    if (threadId) {
      fetchTimeline(id).then(() => {
        timelineLoadedRef.current = true
        const t = useThreadStore.getState().threads.find((th) => th.id === id)
        if (t && (t.exchange_count ?? 0) > 0) {
          track('thread_revisited', { thread_id: id, exchange_count: t.exchange_count })
        }
      })
      fetchHighlights(id)
    }
    return () => {
      useChatStore.getState().clearChat()
      useHighlightStore.getState().clearHighlights()
    }
  }, [threadId, id, fetchTimeline, fetchHighlights])

  // Auto-delete thread on navigate-away if the user never sent a message or added content
  useEffect(() => {
    return () => {
      if (
        timelineLoadedRef.current &&
        entriesRef.current.length === 0 &&
        !streamingRef.current &&
        !hasImportedRef.current
      ) {
        useThreadStore.getState().deleteThread(id)
      }
    }
  }, [id])

  // Per-card polling in ContentItemCard handles content processing status
  // with appropriate timeouts per content type (5 min default, 15 min for PDF/notecard)

  // Scroll to highlighted entry once entries load
  useEffect(() => {
    if (!highlightEntryId || !highlightActive || hasScrolledToHighlight || entries.length === 0) return

    const idx = entryIds.indexOf(highlightEntryId)
    if (idx !== -1) {
      setCurrentHighlightIndex(idx)
      const el = entryRefs.current[highlightEntryId]
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 100)
        setHasScrolledToHighlight(true)
      }
    }
  }, [highlightEntryId, highlightActive, hasScrolledToHighlight, entries, entryIds])

  // Detect user scroll intent via touch/wheel — immediately yields control
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const handleUserIntent = () => {
      if (streaming) {
        userHasScrolledRef.current = true
      }
    }

    const handleScroll = () => {
      const threshold = 80
      const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold
      isAtBottomRef.current = isNearBottom

      // Don't auto-re-engage when user scrolls near bottom — they're reading.
      // Auto-scroll only re-engages via the "Follow" pill tap or stream end.
      if (!isNearBottom && streaming) {
        setShowScrollPill(true)
      } else if (isNearBottom) {
        setShowScrollPill(false)
      }
    }

    el.addEventListener('touchstart', handleUserIntent, { passive: true })
    el.addEventListener('wheel', handleUserIntent, { passive: true })
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      el.removeEventListener('touchstart', handleUserIntent)
      el.removeEventListener('wheel', handleUserIntent)
      el.removeEventListener('scroll', handleScroll)
    }
  }, [streaming])

  // Reset scroll state when streaming ends
  useEffect(() => {
    if (!streaming) {
      userHasScrolledRef.current = false
      setShowScrollPill(false)
    }
  }, [streaming])

  // Auto-scroll for new messages (only when not in highlight mode and user hasn't taken over)
  useEffect(() => {
    if (highlightActive) return
    if (userHasScrolledRef.current) return
    if (!isAtBottomRef.current) return
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'instant' })
  }, [entries, streamingText, highlightActive])

  const scrollToEntry = (index: number) => {
    const entryId = entryIds[index]
    if (entryId == null) return
    const el = entryRefs.current[entryId]
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    setCurrentHighlightIndex(index)
  }

  const handlePrevHighlight = () => {
    if (currentHighlightIndex > 0) {
      scrollToEntry(currentHighlightIndex - 1)
    }
  }

  const handleNextHighlight = () => {
    if (currentHighlightIndex < entryIds.length - 1) {
      scrollToEntry(currentHighlightIndex + 1)
    }
  }

  const handleDismissHighlight = () => {
    setHighlightActive(false)
    setSearchParams({}, { replace: true })
  }

  const handleBack = () => {
    if (cameFromSearch) {
      navigate(-1)
    } else {
      navigate('/research')
    }
  }

  const contentItemCount = entries.filter((e) => e.content_item !== null).length

  const handleScrollToEntry = (entryId: number, selectedText?: string) => {
    const el = entryRefs.current[entryId]
    if (!el) return

    if (selectedText && scrollRef.current) {
      // Scroll entry into view first, then adjust to center the highlighted text
      el.scrollIntoView({ behavior: 'instant', block: 'center' })
      setTimeout(() => {
        if (!scrollRef.current) return
        const range = findTextRange(scrollRef.current, selectedText)
        if (range) {
          const rect = range.getBoundingClientRect()
          const containerRect = scrollRef.current.getBoundingClientRect()
          const offset = rect.top - containerRect.top - containerRect.height / 2
          scrollRef.current.scrollBy({ top: offset, behavior: 'smooth' })
        }
      }, 50)
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const handleScrollToBottom = useCallback(() => {
    userHasScrolledRef.current = false
    isAtBottomRef.current = true
    setShowScrollPill(false)
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'instant' })
  }, [])

  const handleSend = (message: string) => {
    hasImportedRef.current = true
    isAtBottomRef.current = true
    userHasScrolledRef.current = false
    sendMessage(id, message)
  }

  const handleImport = () => {
    hasImportedRef.current = true
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-surface border-b border-elevated shrink-0">
        <button onClick={handleBack} aria-label="Back to Research" className="text-text-secondary hover:text-text-primary">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        {editingTitle ? (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <input
              ref={titleInputRef}
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={handleTitleKeyDown}
              className="flex-1 min-w-0 bg-bg border border-text-muted/30 rounded-lg px-2 py-1 text-sm font-medium text-text-primary outline-none focus:border-accent"
            />
            {previousTitle ? (
              <button
                onMouseDown={(e) => { e.preventDefault(); suppressBlurRef.current = true }}
                onTouchStart={() => { suppressBlurRef.current = true }}
                onClick={handleRevertTitle}
                className="shrink-0 min-w-[4rem] px-2.5 py-1 text-xs font-medium rounded-full bg-text-muted/20 text-text-secondary hover:bg-text-muted/30 transition-colors text-center"
              >
                Revert
              </button>
            ) : (
              <button
                onMouseDown={(e) => { e.preventDefault(); suppressBlurRef.current = true }}
                onTouchStart={() => { suppressBlurRef.current = true }}
                onClick={handleAutoRename}
                disabled={autoRenaming}
                className="shrink-0 min-w-[4rem] px-2.5 py-1 text-xs font-medium rounded-full bg-accent/20 text-accent hover:bg-accent/30 transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {autoRenaming ? (
                  <span className="inline-block w-3 h-3 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                ) : 'Auto'}
              </button>
            )}
          </div>
        ) : (
          <div className="flex-1 min-w-0 cursor-pointer" onClick={handleTitleTap}>
            <h1 className="font-medium truncate">
              {thread?.title || 'Conversation'}
            </h1>
            {thread?.external_source === 'chatgpt' && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30 w-fit">
                Imported from ChatGPT
              </span>
            )}
            <div className="flex items-center gap-2">
              {thread && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowPresetPicker(!showPresetPicker); setShowModelPicker(false) }}
                  className="text-[10px] text-text-muted hover:text-text-secondary transition-colors"
                >
                  {thread.preset_name || 'No preset'}
                </button>
              )}
              {thread?.ai_model && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowModelPicker(!showModelPicker); setShowPresetPicker(false) }}
                  className="text-[10px] text-accent/70 hover:text-accent transition-colors"
                >
                  {thread.ai_model}
                </button>
              )}
            </div>
          </div>
        )}
        {highlights.length > 0 && (
          <button
            onClick={() => setHighlightsOpen(true)}
            className="relative text-text-secondary hover:text-text-primary p-1 transition-colors"
            aria-label={`${highlights.length} highlight${highlights.length === 1 ? '' : 's'}`}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
            </svg>
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-yellow-500 text-[10px] font-bold text-black px-1">
              {highlights.length}
            </span>
          </button>
        )}
        {contentItemCount > 0 && (
          <button
            onClick={() => setAttachmentsOpen(true)}
            className="relative text-text-secondary hover:text-text-primary p-1 transition-colors"
            aria-label={`${contentItemCount} attachment${contentItemCount === 1 ? '' : 's'}`}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
            </svg>
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-accent text-[10px] font-bold text-white px-1">
              {contentItemCount}
            </span>
          </button>
        )}
        {thread && <DocsStatus thread={thread} />}
      </div>

      {/* Model picker dropdown */}
      {showModelPicker && thread && availableModels.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-4 py-2 bg-surface border-b border-elevated shrink-0">
          {availableModels.map((m) => (
            <button
              key={m}
              disabled={streaming}
              onClick={async () => {
                if (m !== thread.ai_model) {
                  await useThreadStore.getState().setThreadModel(thread.id, m)
                }
                setShowModelPicker(false)
              }}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors disabled:opacity-50 ${
                m === thread.ai_model
                  ? 'bg-accent/20 text-accent border border-accent/40'
                  : 'bg-elevated text-text-muted border border-transparent hover:text-text-secondary'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      )}

      {/* Preset picker dropdown */}
      {showPresetPicker && thread && (
        <div className="flex flex-wrap gap-1.5 px-4 py-2 bg-surface border-b border-elevated shrink-0">
          <button
            disabled={streaming}
            onClick={async () => {
              if (thread.system_prompt_preset !== null) {
                await useThreadStore.getState().setThreadPreset(thread.id, null)
              }
              setShowPresetPicker(false)
            }}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors disabled:opacity-50 ${
              thread.system_prompt_preset === null
                ? 'bg-accent/20 text-accent border border-accent/40'
                : 'bg-elevated text-text-muted border border-transparent hover:text-text-secondary'
            }`}
          >
            No preset
          </button>
          {presets.map((p) => (
            <button
              key={p.id}
              disabled={streaming}
              onClick={async () => {
                if (p.id !== thread.system_prompt_preset) {
                  await useThreadStore.getState().setThreadPreset(thread.id, p.id)
                }
                setShowPresetPicker(false)
              }}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors disabled:opacity-50 ${
                p.id === thread.system_prompt_preset
                  ? 'bg-accent/20 text-accent border border-accent/40'
                  : 'bg-elevated text-text-muted border border-transparent hover:text-text-secondary'
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4 relative">
        {entries.length === 0 && !streaming && (
          <div className="flex flex-col items-center justify-center h-full text-text-muted">
            <svg className="w-12 h-12 mb-3 opacity-40" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
            <p className="text-sm">Ask a question to get started</p>
          </div>
        )}

        {entries.map((entry) => {
          const isHighlighted = highlightActive && entry.id === entryIds[currentHighlightIndex]

          // Render content items (non-exchange entries)
          if (entry.content_item) {
            return (
              <div
                key={entry.id}
                ref={(el) => { entryRefs.current[entry.id] = el }}
                className={`transition-all duration-500 rounded-2xl ${
                  isHighlighted ? 'ring-2 ring-accent bg-accent/5' : ''
                }`}
              >
                <ContentItemCard item={entry.content_item} threadId={id} entryId={entry.id} />
              </div>
            )
          }

          // Render exchanges
          if (entry.exchange) {
            return (
              <div
                key={entry.id}
                ref={(el) => { entryRefs.current[entry.id] = el }}
                className={`transition-all duration-500 rounded-2xl ${
                  isHighlighted ? 'ring-2 ring-accent p-1' : ''
                }`}
              >
                <MessageBubble role="user" content={entry.exchange.user_message} />
                {entry.exchange.ai_response && (
                  <div data-exchange-id={entry.exchange.id}>
                    <MessageBubble
                      role="assistant"
                      content={entry.exchange.ai_response}
                      model={entry.exchange.model_used}
                      streaming={continuingEntryId === entry.id}
                      isHighlighted={highlights.some((h) => h.exchange_id === entry.exchange!.id)}
                      onHighlight={(selectedText) => {
                        const ex = entry.exchange!
                        const existing = highlights.find((h) => h.exchange_id === ex.id && h.selected_text === selectedText)
                        if (existing) {
                          deleteHighlight(id, existing.id)
                        } else {
                          createHighlight(id, { exchange_id: ex.id, selected_text: selectedText })
                        }
                      }}
                    />
                  </div>
                )}
                {!streaming && (!entry.exchange.ai_response || interruptedEntryId === entry.id) && (
                  <div className="flex items-center gap-3 mt-1 ml-1 py-2">
                    <span className="text-xs text-error">
                      {entry.exchange.ai_response ? 'Response interrupted' : 'Response failed'}
                    </span>
                    <button
                      onClick={() => retryStream(id, entry.id)}
                      className="text-xs text-accent hover:text-accent-hover transition-colors"
                    >
                      Retry
                    </button>
                  </div>
                )}
                {!streaming && truncatedEntryId === entry.id && (
                  <div className="flex items-center gap-3 mt-1 ml-1 py-2">
                    <span className="text-xs text-text-muted">Response was cut off</span>
                    <button
                      onClick={() => continueStream(id, entry.id)}
                      className="text-xs font-medium text-accent hover:text-accent-hover transition-colors"
                    >
                      Continue
                    </button>
                  </div>
                )}
              </div>
            )
          }

          return null
        })}

        {/* Thinking indicator -- waiting for first token */}
        {streaming && !streamingText && !continuingEntryId && <ThinkingIndicator />}

        {/* Streaming response */}
        {streaming && streamingText && (
          <MessageBubble role="assistant" content={streamingText} streaming />
        )}
        {error && (
          <div className="text-center text-error text-sm py-2">{error}</div>
        )}

        <SelectionToolbar
          containerRef={scrollRef}
          onSave={(selectedText) => {
            // Find which exchange the selection is in by walking up from the selection
            const sel = window.getSelection()
            if (!sel || sel.rangeCount === 0) return
            const node = sel.getRangeAt(0).commonAncestorContainer
            const entryEl = (node instanceof HTMLElement ? node : node.parentElement)?.closest('[data-exchange-id]')
            const exchangeId = entryEl?.getAttribute('data-exchange-id')
            if (!exchangeId || !selectedText.trim()) return
            // Don't create a duplicate highlight for the same text in the same exchange.
            const exists = highlights.some(
              (h) => h.exchange_id === Number(exchangeId) && h.selected_text === selectedText
            )
            if (exists) return
            createHighlight(id, { exchange_id: Number(exchangeId), selected_text: selectedText })
          }}
        />
      </div>

      {/* Scroll to bottom pill */}
      {showScrollPill && (
        <div className="relative">
          <button
            onClick={handleScrollToBottom}
            className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface border border-elevated shadow-lg text-xs text-text-secondary hover:text-text-primary transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3" />
            </svg>
            Follow
          </button>
        </div>
      )}

      {/* Bottom bar: highlight nav or chat input */}
      {highlightActive ? (
        <HighlightNav
          currentIndex={currentHighlightIndex}
          total={entryIds.length}
          onPrev={handlePrevHighlight}
          onNext={handleNextHighlight}
          onDismiss={handleDismissHighlight}
        />
      ) : (
        <ChatInput key={id} onSend={handleSend} disabled={streaming} threadId={id} onImport={handleImport} />
      )}

      {/* Attachments / Sources panel */}
      <AttachmentsPanel
        open={attachmentsOpen}
        entries={entries}
        onClose={() => setAttachmentsOpen(false)}
        onScrollToEntry={handleScrollToEntry}
      />

      {/* Highlights sheet */}
      <HighlightsSheet
        open={highlightsOpen}
        highlights={highlights}
        onClose={() => setHighlightsOpen(false)}
        onScrollToEntry={handleScrollToEntry}
        onDeleteHighlight={(hId) => deleteHighlight(id, hId)}
        onUpdateNote={(hId, note) => updateHighlight(id, hId, { note })}
      />
    </div>
  )
}
